import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { ReactComponent as AddCircleSVG } from 'assets/icons/add-circle.svg';
import { AxiosResponse } from 'axios';
import classNames from 'classnames';
import dayjs from 'dayjs';
import {
	addApartment,
	addSingleReceiptOtherService,
	getApartmentList,
	getApartmentsSelector,
	getBillReceiptSelector,
	getIsUserHasFullAccessSelector,
	getIsUserHasHostCard,
	getSingleReceipt,
	getUserProfileSelector,
	paySingleReceipt,
	resetDocumentViewer,
	resetReceipt,
	showToast,
	updateUserProfile,
	useAppDispatch,
	useAppSelector,
} from 'store';
import { PaymentMethodModal } from 'components/PaymentMethodModal';
import { PaymentPayerForm } from 'components/PaymentPayerForm';
import {
	AddPortmoneServiceModal,
	ReceiptAvailablePaymentMethodsFilter,
	ReceiptPaymentInstruction,
	ReceiptPortmoneDefaultServiceList,
	ReceiptPortmoneServiceList,
	ReceiptPreview,
	ReceiptProviderServiceList,
	ReceiptProvidersListPreview,
} from 'components/Receipt';
import { CustomButton, Loader, PayerDataDetailsModalContent } from 'components/shared';
import {
	CONSOLIDATED_PAYMENT_INSTRUCTION_TITLE,
	FIREBASE_EVENT_ANALYTICS_BUTTON,
	FIREBASE_EVENT_ANALYTICS_PAGE,
	GOOGLE_PAY_MAX_AMOUNT_PORTMONE,
	GOOGLE_PAY_MAX_AMOUNT_SERVICES,
	PAYMENT_INSTRUCTION_TITLE,
} from 'utils/constants';
import {
	ERROR_CODE,
	ERROR_MESSAGE,
	PAY_TOKEN_TYPE,
	PAYMENT_PROVIDER,
	PROVIDER_TYPE,
	ROUTER_URL,
	STORAGE_KEY,
} from 'utils/enums';
import {
	checkIsKharkivRegionApartment,
	convertCoinsToUAH,
	convertServiceProviderDataUAHToCoins,
	convertUAHToCoins,
	decryptData,
	findProviderSelectedServiceInSingleReceipt,
	getApartmentDataFromReceipt,
	getAppartmentErrorMessage,
	getChangedPayerProfileData,
	getData3DS,
	getDefaultPaymentMethodGroup,
	getErrorMessage,
	getErrorResponse,
	getOtherPortmoneAmountFromTariffItems,
	getOtherProviderServicePayment,
	getPayerFullName,
	getPaymentPayerInitialValues,
	getPaymentPayerServiceFieldsData,
	getProviderServicePayment,
	getReceiptPaymentRecipientCount,
	getReceiptPaymentTotalAmount,
	getReceiptRequestBody,
	getReceiptReturnUrl,
	getReceiptRoute,
	getReceiptStatusRoute,
	getStaticTotalFeeTitle,
	getUserFullName,
	removeReceiptList,
	syncSelectedPortmoneServices,
} from 'utils/helpers';
import { useOpen } from 'utils/hooks';
import {
	CardService,
	FirebaseAnalytics,
	HostCardMessageService,
	RadabankService,
	StorageService,
} from 'utils/services';
import {
	IAddOtherServiceToSingleReceipt,
	IApartmentResponse,
	IBillApartments,
	IBillReceipt,
	ICardRequestResponse,
	IPaymentPayerFormData,
	IPaymentResponse,
	IReceiptPayRequest,
	IReceiptResponse,
	ISingleReceiptPayProviderServiceRequest,
	IUserProfileResponse,
	PaymentMethodsKeys,
	ReceiptOtherServiceTariffItems,
	StringOrNull,
} from 'utils/types';
import styles from './index.module.scss';

type ReceiptPageParams = {
	apartmentAccountId: string;
};

type ReceiptPaymentStep = 'selection' | 'payer' | 'instruction';

const PAYMENT_STEP_MAP_TITLE: Partial<Record<ReceiptPaymentStep, string>> = {
	payer: 'Введіть дані платника',
};

const RECEIPT_PAYER_FORM_ID = 'receipt-payer-form';

export const ReceiptPage: FC = () => {
	const dispatch = useAppDispatch();

	const history = useHistory();
	const params = useParams<ReceiptPageParams>();

	const paymentModal = useOpen();
	const otherServicesModal = useOpen();

	const receipt: IBillReceipt = useAppSelector(getBillReceiptSelector);
	const profile: IUserProfileResponse | null = useAppSelector(getUserProfileSelector);
	const apartment: IBillApartments = useAppSelector(getApartmentsSelector);
	const isHasFullAccess = useAppSelector(getIsUserHasFullAccessSelector);
	const isUserHasHostCard = useAppSelector(getIsUserHasHostCard);

	const [selectedServiceList, setSelectedServiceList] = useState<ISingleReceiptPayProviderServiceRequest[]>([]);
	const [paymentSum, setPaymentSum] = useState(0);
	const [receiptPayRequest, setReceiptPayRequest] = useState<IReceiptPayRequest | null>(null);
	const [selectedServicesLength, setSelectedServiceLength] = useState({ portmone: 0, default: 0 });
	const [feeList, setFeeList] = useState<
		{ serviceId: string | number; amount: number; hostCardAmount: number; billId?: string }[]
	>([]);
	const [totalFeeAmount, setTotalFeeAmount] = useState(0);
	const [apartmentAccountId, setApartmentAccountId] = useState<null | number>(null);
	const [paymentStep, setPaymentStep] = useState<ReceiptPaymentStep>('selection');
	const [paymentPayerData, setPaymentPayerData] = useState<IPaymentPayerFormData | null>(null);
	const [isPayerFormValid, setIsPayerFormValid] = useState(false);
	const [storedSelectedServiceList, setStoredSelectedServiceList] = useState<
		ISingleReceiptPayProviderServiceRequest[] | null
	>(null);

	const [activePaymentMethodGroup, setActivePaymentMethodGroup] = useState<PaymentMethodsKeys>(() =>
		getDefaultPaymentMethodGroup(isUserHasHostCard)
	);

	const totalHostCardFeeAmount = useMemo(() => {
		return feeList.reduce((acc, item) => {
			const isServiceSelected = selectedServiceList
				.filter((item) => +item.incomeBalance > 0)
				.some((selectedItem) => {
					const isEqualById = selectedItem.serviceId === item.serviceId || selectedItem.id === item.serviceId;

					if (selectedItem.billId && item.billId) {
						return isEqualById && selectedItem.billId === item.billId;
					}
					return isEqualById;
				});
			return isServiceSelected ? acc + item.hostCardAmount : acc;
		}, 0);
	}, [feeList, selectedServiceList]);

	const paymentInstructionTitle = useMemo(() => {
		if (!receipt.data) return PAYMENT_INSTRUCTION_TITLE;

		return getReceiptPaymentRecipientCount(receipt.data, selectedServiceList) > 1
			? CONSOLIDATED_PAYMENT_INSTRUCTION_TITLE
			: PAYMENT_INSTRUCTION_TITLE;
	}, [receipt.data, selectedServiceList]);
	const isPayerDataRequired = useMemo(
		() =>
			selectedServiceList.some(
				(item) =>
					Number(item.incomeBalance) > 0 &&
					(item.serviceId !== undefined || item.providerType === PROVIDER_TYPE.PORTMONE)
			),
		[selectedServiceList]
	);
	const paymentRequestPayerData = isPayerDataRequired ? paymentPayerData : null;

	useEffect(() => {
		setActivePaymentMethodGroup(getDefaultPaymentMethodGroup(isUserHasHostCard));
	}, [isUserHasHostCard]);

	useEffect(() => {
		const apartmentAccountId = decryptData(params.apartmentAccountId);

		if (apartmentAccountId) {
			setApartmentAccountId(+apartmentAccountId);
			setStorageValue();
			HostCardMessageService.setNextActiveMessage();
		} else {
			history.replace(ROUTER_URL.NOT_FOUND);
		}

		return () => {
			dispatch(resetReceipt());
		};
	}, []);

	useEffect(() => {
		if (!apartment.list.length && profile && apartmentAccountId) {
			getUserApartments(profile.userId, apartmentAccountId);
		}
		if (profile && apartmentAccountId && (receipt.data?.apartmentAccountId !== apartmentAccountId || !receipt.data)) {
			getReceipt(apartmentAccountId, profile.userId);
		}
	}, [apartmentAccountId, profile]);

	useEffect(() => {
		if (receipt.data && !selectedServiceList.length && !feeList.length) {
			const receiptServicesListRequestData: ISingleReceiptPayProviderServiceRequest[] = Object.values(
				receipt.data.serviceProviderData
			)
				.flat()
				.map(getProviderServicePayment)
				// Selects only services which are allowed to be payed for with activePaymentMethodGroup
				.filter((item) => +item.incomeBalance > 0 && item.paymentMethods[activePaymentMethodGroup]);
			const receiptOtherServicesListRequestData: ISingleReceiptPayProviderServiceRequest[] =
				receipt.data.otherPaymentServices
					.map((item) =>
						getOtherProviderServicePayment(
							item,
							getOtherPortmoneAmountFromTariffItems(
								item.tarifItems as ReceiptOtherServiceTariffItems | ReceiptOtherServiceTariffItems[]
							)
								? getOtherPortmoneAmountFromTariffItems(
										item.tarifItems as ReceiptOtherServiceTariffItems | ReceiptOtherServiceTariffItems[]
								  ).toString()
								: (item.amount - item.payed).toString()
						)
					)
					// Selects only services which are allowed to be payed for with activePaymentMethodGroup
					.filter(
						(item: ISingleReceiptPayProviderServiceRequest) =>
							+item.incomeBalance > 0 && item.paymentMethods[activePaymentMethodGroup]
					);
			const selectedServicesListPayload = [...receiptServicesListRequestData, ...receiptOtherServicesListRequestData];
			setSelectedServiceList(selectedServicesListPayload);
		}
	}, [receipt.data, feeList.length, activePaymentMethodGroup]);

	useEffect(() => {
		if (!receipt.data) return;

		if (storedSelectedServiceList) {
			setSelectedServiceList(
				syncSelectedPortmoneServices(storedSelectedServiceList, receipt.data.serviceProviderData)
			);
			setStoredSelectedServiceList(null);
			return;
		}

		setSelectedServiceList((currentList) =>
			syncSelectedPortmoneServices(currentList, receipt.data?.serviceProviderData ?? {})
		);
	}, [receipt.data?.serviceProviderData, storedSelectedServiceList]);

	useEffect(() => {
		if (receipt.data) {
			dispatch(resetDocumentViewer());
			setPaymentStep('selection');
			if (selectedServiceList.length) {
				setSelectedServiceList([]);
				setFeeList([]);
			}
		}
	}, [receipt.data?.id]);

	useEffect(() => {
		const selectedServicesLength = selectedServiceList
			.filter((item) => +item.incomeBalance > 0)
			.reduce(
				(acc, item) => {
					const payload = { ...acc };
					if (item.providerType === PROVIDER_TYPE.PORTMONE) {
						payload.portmone += 1;
					} else {
						payload.default += 1;
					}
					return payload;
				},
				{ portmone: 0, default: 0 }
			);
		setSelectedServiceLength(selectedServicesLength);
		setPaymentSum(getReceiptPaymentTotalAmount(selectedServiceList));
	}, [selectedServiceList]);

	useEffect(() => {
		if (profile && receipt.data) {
			handleSetRequestBody(
				profile,
				receipt.data,
				selectedServiceList,
				paymentSum,
				totalFeeAmount,
				paymentRequestPayerData
			);
		}
	}, [selectedServiceList, profile, paymentSum, receipt.data, totalFeeAmount, paymentRequestPayerData]);

	useEffect(() => {
		const payload = feeList.reduce((acc, item) => {
			const selectedNotZeroAmountServices = selectedServiceList.filter((selectedItem) => {
				if (Number(selectedItem.incomeBalance) <= 0) {
					return false;
				}
				const isEqualById = selectedItem.serviceId === item.serviceId || selectedItem.id === item.serviceId;

				if (selectedItem.billId && item.billId) {
					return isEqualById && selectedItem.billId === item.billId;
				}
				return isEqualById;
			});
			return selectedNotZeroAmountServices.length ? acc + item.amount : acc;
		}, 0);
		setTotalFeeAmount(payload);
	}, [feeList, selectedServiceList]);

	const setStorageValue = async (): Promise<void> => {
		const data = await removeReceiptList();
		if (data) {
			setStoredSelectedServiceList(data);
		}
	};

	const getUserApartments = async (userId: string, apartmentAccountId: number): Promise<void> => {
		try {
			const response = await dispatch(getApartmentList(userId)).unwrap();
			await addUserApartments(response.listApartment, apartmentAccountId, userId);
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const getReceipt = async (apartmentAccountId: number, userId: string): Promise<void> => {
		try {
			await dispatch(getSingleReceipt(getReceiptRequestBody(apartmentAccountId, userId))).unwrap();

			if (apartment.list.length) {
				await addUserApartments(apartment.list, apartmentAccountId, userId);
			}
		} catch (error) {
			const errorResponse = getErrorResponse(error);

			if (errorResponse?.errorData?.code === ERROR_CODE.RECEIPT_NOT_FOUND) {
				dispatch(showToast({ message: ERROR_MESSAGE.RECEIPT_NOT_FOUND }));
				history.push(ROUTER_URL.NOT_FOUND);
			} else {
				dispatch(showToast({ message: getAppartmentErrorMessage(error) }));
			}
		}
	};

	const addUserApartments = async (
		list: IApartmentResponse[],
		apartmentAccountId: number,
		userId: string
	): Promise<void> => {
		const isUserHasApartment = list.find((item) => item.apartmentAccountId === apartmentAccountId);
		if (!isUserHasApartment) {
			await dispatch(addApartment({ apartmentAccountId, userId })).unwrap();
			dispatch(showToast({ message: 'Адреса збережена в обліковому записі', color: 'success' }));
		}
	};

	const handleChangeSelectedList = (serviceList: ISingleReceiptPayProviderServiceRequest[]): void => {
		setSelectedServiceList(serviceList);
	};

	const handleClickPay = async (): Promise<void> => {
		try {
			if (paymentStep === 'payer') {
				(document.getElementById(RECEIPT_PAYER_FORM_ID) as HTMLFormElement | null)?.requestSubmit();
				return;
			}
			if (!receiptPayRequest) {
				throw new Error(`${ERROR_MESSAGE.SOMETHING_WENT_WRONG}, спробуйте пізніше`);
			}
			if (convertCoinsToUAH(receiptPayRequest.amount) < 10) {
				throw new Error('Мінімальна сума платежу 10₴');
			}
			if (paymentStep === 'selection') {
				setPaymentStep(isPayerDataRequired ? 'payer' : 'instruction');
				return;
			}
			paymentModal.handleOpen();
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const handleSubmitPayerForm = async (data: IPaymentPayerFormData): Promise<void> => {
		try {
			if (!profile) {
				throw new Error('Не вдалося отримати дані профілю');
			}

			const changedProfileData = getChangedPayerProfileData(profile, data);
			if (changedProfileData) {
				await dispatch(updateUserProfile(changedProfileData)).unwrap();
			}

			setPaymentPayerData(data);
			setPaymentStep('instruction');
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const handlePayByCard = async (cardFromId: string, provider: StringOrNull): Promise<void> => {
		if (receiptPayRequest) {
			let reqBody: IReceiptPayRequest = {
				...receiptPayRequest,
				returnUrl: getReceiptReturnUrl(params.apartmentAccountId, process.env.REACT_APP_URL),
				cardFromId,
				data3DS2: getData3DS(),
			};

			if (provider === PAYMENT_PROVIDER.RADABANK) {
				const radabankData = await RadabankService.getInternalReqPayload({ userId: receiptPayRequest.userId });
				const deviceData = await RadabankService.getReqPayload({});

				reqBody = {
					...reqBody,
					payToken: btoa(
						JSON.stringify({ ...radabankData, userId: undefined, ...deviceData })
					),
					payTokenType: PAY_TOKEN_TYPE.RADABANK,
					fee: convertUAHToCoins(totalHostCardFeeAmount).toString(),
				};
			}

			const responseData: IPaymentResponse[] = await dispatch(paySingleReceipt(reqBody)).unwrap();
			history.push(getReceiptStatusRoute(params.apartmentAccountId, responseData[0].billId));
		}
	};

	const handlePayByWallet = async (payToken: string, payTokenType: PAY_TOKEN_TYPE): Promise<void> => {
		try {
			if (receiptPayRequest) {
				const reqBody: IReceiptPayRequest = {
					...receiptPayRequest,
					payToken,
					payTokenType,
					data3DS2: getData3DS(),
					returnUrl: getReceiptReturnUrl(params.apartmentAccountId, process.env.REACT_APP_URL),
				};
				const responseData: IPaymentResponse[] = await dispatch(paySingleReceipt(reqBody)).unwrap();
				history.push(getReceiptStatusRoute(params.apartmentAccountId, responseData[0].billId));
			}
		} catch (err) {
			dispatch(showToast({ message: getErrorMessage(err) }));
		}
	};

	const handlePayByAnotherCard = async (): Promise<void> => {
		if (receiptPayRequest) {
			if (
				(selectedServicesLength.default && !selectedServicesLength.portmone) ||
				(!selectedServicesLength.default && selectedServicesLength.portmone === 1)
			) {
				const response: IPaymentResponse[] = await dispatch(paySingleReceipt(receiptPayRequest)).unwrap();
				const paymentRedirectUrl = response[0].link;
				if (Capacitor.isNativePlatform()) {
					await Browser.open({ url: paymentRedirectUrl });
				} else {
					window.location.href = paymentRedirectUrl;
				}
				// portmone and default services possible to pay only by added card
			} else {
				await StorageService.set(STORAGE_KEY.RECEIPT_SELECTED_LIST, selectedServiceList);
				const createCardResponse: AxiosResponse<ICardRequestResponse> = await CardService.createRequest({
					userId: profile?.userId as string,
					// get value from parameters because need hashed value for a routing
					returnUrl: `${process.env.REACT_APP_URL}${getReceiptRoute(params.apartmentAccountId)}`,
				});
				// in an app-url-litener we close the browser for ios
				if (Capacitor.isNativePlatform()) {
					await Browser.open({ url: createCardResponse.data.link });
					Browser.addListener('browserFinished', setStorageValue);
				} else {
					window.location.href = createCardResponse.data.link;
				}
			}
		}
	};

	const handleChangeFeeValue = (
		serviceId: string | number,
		feeAmount: number,
		feeHostCardAmount: number,
		billId?: string
	): void => {
		setFeeList((prevState) => {
			let payload = prevState;
			const isServiceInList = payload.find((item) => {
				if (billId && item.billId) {
					return billId === item.billId && item.serviceId === serviceId;
				}
				return item.serviceId === serviceId;
			});
			if (isServiceInList) {
				payload = payload.map((item) =>
					item.serviceId === serviceId
						? { ...item, amount: feeAmount, hostCardAmount: feeHostCardAmount, billId }
						: item
				);
			} else {
				payload = [...payload, { serviceId, amount: feeAmount, hostCardAmount: feeHostCardAmount, billId }];
			}
			return payload;
		});
	};

	const handleSetRequestBody = (
		profile: IUserProfileResponse,
		receiptData: IReceiptResponse,
		selectedServiceList: ISingleReceiptPayProviderServiceRequest[],
		totalAmount: number,
		totalFeeAmount: number,
		payerData: IPaymentPayerFormData | null
	): void => {
		const payerServiceFieldsData = payerData ? getPaymentPayerServiceFieldsData(payerData) : {};
		const serviceProviderData: ISingleReceiptPayProviderServiceRequest[] = convertServiceProviderDataUAHToCoins(
			selectedServiceList
		).map((item) => {
			if (item.serviceId) {
				const otherPaymentService = receiptData.otherPaymentServices.find(
					(service) => service.id === item.serviceId
				);

				return {
						...item,
						serviceFieldsData: {
							...item.serviceFieldsData,
							...(otherPaymentService?.shortId !== undefined
								? { shortId: otherPaymentService.shortId }
								: {}),
							...(otherPaymentService?.reservedBillId
								? { reservedBillId: otherPaymentService.reservedBillId }
								: {}),
							...payerServiceFieldsData,
						},
					};
			}

			return item;
		});
		const hasCommunalPayments = selectedServiceList.some(
			(item) => item.id !== undefined && Number(item.incomeBalance) > 0
		);
		const communalPaymentNotes = Array.from(
			new Set(
				Object.values(receiptData.serviceProviderData)
					.flat()
					.filter((service) =>
						selectedServiceList.some(
							(item) =>
								Number(item.incomeBalance) > 0 &&
								findProviderSelectedServiceInSingleReceipt(service, item)
						)
					)
					.map(
						(service) =>
							service.regDataObject?.serviceTitle ??
							service.regDataObject?.description ??
							service.serviceTitle ??
							''
					)
					.map((value) => value.trim())
					.filter(Boolean)
			)
		).join('; ');
		const payload: IReceiptPayRequest = {
			apartmentAccountId: `${receiptData.apartmentAccountId}`,
			id: receiptData.id,
			month: receiptData.month,
			userId: profile.userId,
			clientName: payerData ? getPayerFullName(payerData) : getUserFullName(profile),
			clientPhone: profile.phone,
			amount: convertUAHToCoins(totalAmount),
			sumBalance: convertUAHToCoins(totalAmount).toString(),
			returnUrl: getReceiptStatusRoute(params.apartmentAccountId, '', process.env.REACT_APP_URL),
			fee: convertUAHToCoins(totalFeeAmount).toString(),
			serviceProviderData,
			...(hasCommunalPayments && receiptData.communalPaymentShortId !== undefined
				? { communalPaymentShortId: receiptData.communalPaymentShortId }
				: {}),
			...(hasCommunalPayments && receiptData.communalPaymentReservedBillId
				? { communalPaymentReservedBillId: receiptData.communalPaymentReservedBillId }
				: {}),
			...(hasCommunalPayments && payerData
				? {
						...payerServiceFieldsData,
						payment_notes: communalPaymentNotes,
				  }
				: {}),
		};
		setReceiptPayRequest(payload);
	};

	const handleAddOtherProviderService = async (reqBody: IAddOtherServiceToSingleReceipt): Promise<void> => {
		try {
			if (profile) {
				FirebaseAnalytics.logEvent(
					FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SINGLE_RECEIPT,
					FIREBASE_EVENT_ANALYTICS_BUTTON.SR_ADD_SERVICE
				);
				await dispatch(addSingleReceiptOtherService(reqBody)).unwrap();
				const receiptResponse = await dispatch(
					getSingleReceipt(getReceiptRequestBody(reqBody.serviceRequestData.apartmentAccountId, profile.userId))
				).unwrap();
				const serviceResponseData = receiptResponse?.otherPaymentServices.find(
					(item) => item.id === reqBody.serviceRequestData.serviceId
				);

				if (serviceResponseData && serviceResponseData.paymentMethods[activePaymentMethodGroup]) {
					const syncedSelectedServiceList = syncSelectedPortmoneServices(
						selectedServiceList,
						receiptResponse.serviceProviderData
					);
					setSelectedServiceList([
						...syncedSelectedServiceList,
						getOtherProviderServicePayment(serviceResponseData, serviceResponseData.amount.toString()),
					]);
					dispatch(showToast({ message: `${serviceResponseData.title} успішно додано до ЄК`, color: 'success' }));
				}
				otherServicesModal.handleClose();
			}
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const handleBeforeGooglePay = () => {
		if (selectedServicesLength.default && paymentSum > GOOGLE_PAY_MAX_AMOUNT_SERVICES) {
			throw new Error(`Не більше ${GOOGLE_PAY_MAX_AMOUNT_SERVICES} грн за один платіж. Кількість платежів необмежена`);
		}
		if (selectedServicesLength.portmone && paymentSum > GOOGLE_PAY_MAX_AMOUNT_PORTMONE) {
			throw new Error(`Не більше ${GOOGLE_PAY_MAX_AMOUNT_PORTMONE} грн за один платіж. Кількість платежів необмежена`);
		}
	};

	const handleClickAddService = () => {
		FirebaseAnalytics.logEvent(
			FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SINGLE_RECEIPT,
			FIREBASE_EVENT_ANALYTICS_BUTTON.SR_CLICK_ADD_SERVICE
		);
		otherServicesModal.handleOpen();
	};

	const handleMapBack = (): void => {
		if (paymentStep === 'instruction') {
			setPaymentStep(isPayerDataRequired ? 'payer' : 'selection');
			return;
		}
		setPaymentStep('selection');
	};

	const allowedMethods = useMemo(() => {
		if (!selectedServiceList.length) return {};

		// const selectedServicesPaymentMethodsKeys = Object.keys(
		// 	selectedServiceList[0]?.paymentMethods
		// ) as (keyof PaymentMethods)[];

		// const result = selectedServicesPaymentMethodsKeys.reduce((acc, curr) => {
		// 	return {
		// 		...acc,
		// 		[curr]: selectedServiceList.every((item) => item?.paymentMethods?.[curr]),
		// 	};
		// }, {});

		return { [activePaymentMethodGroup]: true };
	}, [selectedServiceList, activePaymentMethodGroup]);

	const isKharkivRegionApartment = receipt.data?.region ? checkIsKharkivRegionApartment(receipt.data.region) : false;

	const handleChangeActivePaymentMethodsGroup = (value: PaymentMethodsKeys) => {
		if (!receipt.data || activePaymentMethodGroup === value) return;

		setActivePaymentMethodGroup(value);

		// TODO: move to helper
		const receiptServicesListRequestData: ISingleReceiptPayProviderServiceRequest[] = Object.values(
			receipt.data.serviceProviderData
		)
			.flat()
			.map(getProviderServicePayment)
			.filter((item) => +item.incomeBalance > 0 && item.paymentMethods[value]);
		const receiptOtherServicesListRequestData: ISingleReceiptPayProviderServiceRequest[] =
			receipt.data.otherPaymentServices
				.map((item) =>
					getOtherProviderServicePayment(
						item,
						getOtherPortmoneAmountFromTariffItems(
							item.tarifItems as ReceiptOtherServiceTariffItems | ReceiptOtherServiceTariffItems[]
						)
							? getOtherPortmoneAmountFromTariffItems(
									// eslint-disable-next-line max-len
									item.tarifItems as ReceiptOtherServiceTariffItems | ReceiptOtherServiceTariffItems[]
							  ).toString()
							: (item.amount - item.payed).toString()
					)
				)
				.filter(
					(item: ISingleReceiptPayProviderServiceRequest) => +item.incomeBalance > 0 && item.paymentMethods[value]
				);
		const selectedServicesListPayload = [...receiptServicesListRequestData, ...receiptOtherServicesListRequestData];

		setSelectedServiceList(selectedServicesListPayload);
	};

	return (
		<div className={classNames('page', styles.wrapper)}>
			<ReceiptPreview
				apartmentData={
					receipt.data
						? getApartmentDataFromReceipt(receipt.data)
						: (apartment.list?.find((item) => item.apartmentAccountId === apartmentAccountId) as IApartmentResponse)
				}
				apartmentAccountId={apartmentAccountId}
				amountToPay={paymentSum}
				selectedServicesCount={selectedServiceList.filter((item) => +item.incomeBalance > 0).length}
				onClickPay={handleClickPay}
				isDisabled={
					!(paymentSum + totalFeeAmount) ||
					receipt.isLoading ||
					receipt.data?.isArchived ||
					!isHasFullAccess ||
					(paymentStep === 'payer' && !isPayerFormValid)
				}
				mapTitle={
					paymentStep === 'instruction' ? paymentInstructionTitle : PAYMENT_STEP_MAP_TITLE[paymentStep]
				}
				mapSubtitle={
					paymentStep === 'instruction'
						? `від ${dayjs().format('DD.MM.YYYY')}`
						: undefined
				}
				mapTitleHint={paymentStep === 'payer' ? <PayerDataDetailsModalContent /> : undefined}
				mapTitleHintAnalyticsButton={
					paymentStep === 'payer' ? FIREBASE_EVENT_ANALYTICS_BUTTON.QUESTION_PAYER_DATA : undefined
				}
				onMapBack={paymentStep === 'selection' ? undefined : handleMapBack}
				isHideMapMenu={paymentStep !== 'selection'}
				payButtonLabel={paymentStep === 'payer' ? 'Продовжити' : 'Сплатити'}
				isHidePayButtonSummary={paymentStep === 'payer'}
				isHidePaymentNote={paymentStep === 'payer'}
			>
				{!!receipt.data && paymentStep === 'selection' && (
					<>
						<ReceiptAvailablePaymentMethodsFilter
							activePaymentMethodGroup={activePaymentMethodGroup}
							onChange={handleChangeActivePaymentMethodsGroup}
						/>
						{isKharkivRegionApartment && (
							<ReceiptProviderServiceList
								activePaymentMethodGroup={activePaymentMethodGroup}
								onChangeFee={handleChangeFeeValue}
								pageName={FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SINGLE_RECEIPT}
								selectedServiceList={selectedServiceList}
								onChangeSelectedList={handleChangeSelectedList}
							/>
						)}
						{/* always show static value as 2% */}
						<ReceiptProvidersListPreview
							title={isKharkivRegionApartment ? 'Інші платежі' : 'Платежі за цією адресою'}
							chipTitle={getStaticTotalFeeTitle(2)}
						>
							{/* Services from serviceProviderData fields */}
							<ReceiptPortmoneDefaultServiceList
								isShowHiddenServices={!isKharkivRegionApartment}
								activePaymentMethodGroup={activePaymentMethodGroup}
								selectedServiceList={selectedServiceList}
								onChangeSelectedList={handleChangeSelectedList}
								onChangeFee={handleChangeFeeValue}
							/>
							{/* Services from otherPaymentServices fields */}
							<ReceiptPortmoneServiceList
								activePaymentMethodGroup={activePaymentMethodGroup}
								selectedServiceList={selectedServiceList}
								onChangeSelectedList={handleChangeSelectedList}
								onChangeFee={handleChangeFeeValue}
							/>
							<CustomButton
								fill="clear"
								label="Додати послугу"
								startIcon={<AddCircleSVG />}
								onClick={handleClickAddService}
								disabled={receipt.data?.isArchived || !isHasFullAccess}
							/>
						</ReceiptProvidersListPreview>
					</>
				)}
				{!!receipt.data && paymentStep === 'payer' && profile && (
					<PaymentPayerForm
						initialValues={paymentPayerData ?? getPaymentPayerInitialValues(profile)}
						onSubmit={handleSubmitPayerForm}
						isEmbedded
						isLastNameFirst
						title={null}
						formId={RECEIPT_PAYER_FORM_ID}
						isHideSubmitButton
						onValidityChange={setIsPayerFormValid}
					/>
				)}
				{!!receipt.data && paymentStep === 'instruction' && (
					<ReceiptPaymentInstruction
						receipt={receipt.data}
						selectedServiceList={selectedServiceList}
						payerData={paymentRequestPayerData}
					/>
				)}
			</ReceiptPreview>
			{!!receipt.data && (
				<>
					<PaymentMethodModal
						totalHostCardFeeAmount={totalHostCardFeeAmount}
						pageName={FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SINGLE_RECEIPT}
						isOpen={paymentModal.isOpen}
						onClose={paymentModal.handleClose}
						totalAmount={paymentSum}
						totalFeeAmount={totalFeeAmount}
						onPayByCard={handlePayByCard}
						isShowPaymentData
						onPayByAnotherCard={handlePayByAnotherCard}
						onPayByWallet={handlePayByWallet}
						onBeforeGooglePay={handleBeforeGooglePay}
						allowedMethods={allowedMethods}
					/>
					<AddPortmoneServiceModal
						isOpen={otherServicesModal.isOpen}
						onClose={otherServicesModal.handleClose}
						onSubmit={handleAddOtherProviderService}
					/>
				</>
			)}
			{receipt.isLoading && <Loader />}
		</div>
	);
};
